/* 79e – Meta Pixel custom events
   Covers:
   - Page_View (Shopify native)
   - View_Item_List, View_Item
   - Search
   - Add_To_Wishlist (Super Wishlist button clicks)
   - Wishlist_View (wishlist page/overlay)
   - Add_To_Cart (AJAX + form)
   - Removed_From_Cart (change/update to qty 0 / item removed)
   - Purchase (handled by Shopify/Pixel app – not duplicated here)
   - Subscribe (Klaviyo footer/newsletter)
   - Scroll_Depth_25 / 50 / 75
   - View_Blog_Articles
   - View_Size_Guide (drawer open)
   - Customize_Start / Customize_Complete (HulkApps options)
*/

(function(){
  // --- Configuration ---------------------------------------------------------
  const PIXEL_ID = 'REPLACE_WITH_PIXEL_ID_IF_NEEDED'; // not used directly, just for reference

  // Utility: safe fbq wrapper
  const safeFbq = function(){
    if (typeof fbq !== 'function') return;
    try {
      fbq.apply(window, arguments);
    } catch (e) {
      console && console.warn && console.warn('fbq error', e);
    }
  };

  // Utility: once()
  const once = (fn) => {
    let done = false;
    return function(){
      if (done) return;
      done = true;
      return fn.apply(this, arguments);
    };
  };

  const debounce = (fn, wait) => {
    let t;
    return function(){
      clearTimeout(t);
      const args = arguments;
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  // Utility: DOM ready
  const onReady = (fn) => {
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(fn, 0);
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  };

  // Utility: get context from window.Shopify / window.meta
  const getShopifyContext = () => {
    const ctx = {};
    if (window.Shopify && Shopify.currency) {
      ctx.currency = Shopify.currency.active || Shopify.currency;
    }
    if (window.meta && meta.product) {
      ctx.product_id = meta.product.id;
      ctx.sku = meta.product.sku;
      ctx.handle = meta.product.handle;
      ctx.product_type = meta.product.type;
      ctx.vendor = meta.product.vendor;
      ctx.tags = meta.product.tags;
    }
    if (window.meta && meta.page) {
      ctx.template = meta.page.template;
    }
    return ctx;
  };

  const payloadFromContext = (extra) => Object.assign({
    url: location.pathname + location.search
  }, getShopifyContext(), extra || {});

  // Helpers to detect page types
  const isPDP = () => !!(window.meta && meta.product);
  const isCollection = () => !!(window.meta && meta.collection);
  const isBlog = () => !!(window.meta && meta.blog);

  // --- Scroll Depth ----------------------------------------------------------
  const wireScrollDepth = once(function(){
    let fired25 = false, fired50 = false, fired75 = false;

    const onScroll = debounce(() => {
      const h = document.documentElement;
      const scrollTop = window.scrollY || h.scrollTop;
      const docHeight = Math.max(
        h.scrollHeight, h.offsetHeight,
        document.body ? document.body.scrollHeight : 0
      );
      const winHeight = window.innerHeight || h.clientHeight;
      const scrolled = ((scrollTop + winHeight) / docHeight) * 100;

      const base = payloadFromContext();

      if (!fired25 && scrolled >= 25) {
        fired25 = true;
        safeFbq('trackCustom', 'Scroll_Depth_25', base);
      }
      if (!fired50 && scrolled >= 50) {
        fired50 = true;
        safeFbq('trackCustom', 'Scroll_Depth_50', base);
      }
      if (!fired75 && scrolled >= 75) {
        fired75 = true;
        safeFbq('trackCustom', 'Scroll_Depth_75', base);
      }
    }, 250);

    window.addEventListener('scroll', onScroll);
    onScroll();
  });

  // --- View Size Guide -------------------------------------------------------
  const wireSizeGuide = once(function(){
    document.addEventListener('click', (e) => {
      const el = e.target.closest('[data-size-guide], .js-size-guide, [href*="#size-guide"]');
      if (!el) return;
      safeFbq('trackCustom', 'View_Size_Guide', payloadFromContext());
    }, true);
  });

  // --- Search ---------------------------------------------------------------
  const wireSearch = once(function(){
    // 1) Theme search forms
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.matches('form[action*="/search"]')) return;
      const qInput = form.querySelector('input[name="q"], input[type="search"]');
      if (!qInput || !qInput.value) return;
      safeFbq('trackCustom', 'Search', payloadFromContext({ search_term: qInput.value }));
    }, true);

    // 2) Instant search or predictive search could be wired similarly if needed
  });

  // --- Wishlist -------------------------------------------------------------
  const wireWishlist = once(function(){
    // Buttons that add to wishlist (SuperWishlist, etc.)
    const wishlistSelector = '[data-sw-wishlist-button], [data-wishlist-add], .js-add-to-wishlist';

    document.addEventListener('click', (e) => {
      const btn = e.target.closest(wishlistSelector);
      if (!btn) return;

      const productId = btn.getAttribute('data-product-id') ||
                        (window.meta && meta.product && meta.product.id);
      const base = payloadFromContext({ product_id: productId });

      safeFbq('trackCustom', 'Add_To_Wishlist', base);
    }, true);

    // Dedicated wishlist page / overlay
    if (/wishlist/i.test(location.pathname)) {
      safeFbq('trackCustom', 'Wishlist_View', payloadFromContext());
    }

    // If wishlist drawer overlays are used, you can listen to a custom event:
    document.addEventListener('wishlist:open', () => {
      safeFbq('trackCustom', 'Wishlist_View', payloadFromContext());
    });
  });

  // --- Add to Cart ----------------------------------------------------------
  const wireAddToCart = once(function(){
    // 1) AJAX cart / fetch
    const _fetch = window.fetch;
    window.fetch = function(input, init){
      const url = (typeof input === 'string') ? input : (input && input.url) || '';
      if (/cart\/add\.js/i.test(url)) {
        try {
          const body = init && init.body;
          let qty = 1, id = null;
          if (body instanceof FormData) {
            qty = parseInt(body.get('quantity') || '1', 10);
            id = body.get('id');
          } else if (typeof body === 'string') {
            const params = new URLSearchParams(body);
            qty = parseInt(params.get('quantity') || '1', 10);
            id = params.get('id');
          }
          safeFbq('trackCustom', 'Add_To_Cart', payloadFromContext({ quantity: qty, variant_id: id }));
        } catch (e) {
          console && console.warn && console.warn('ATC parse error', e);
        }
      }
      return _fetch.apply(this, arguments);
    };

    // 2) Non-AJAX forms (full page reload)
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.matches('form[action*="/cart/add"]')) return;
      const qtyInput = form.querySelector('[name="quantity"]');
      const idInput = form.querySelector('[name="id"]');
      const qty = qtyInput ? parseInt(qtyInput.value || '1', 10) : 1;
      const id = idInput && idInput.value;
      safeFbq('trackCustom', 'Add_To_Cart', payloadFromContext({ quantity: qty, variant_id: id }));
    }, true);
  });

  // --- Remove from Cart -----------------------------------------------------
  const wireRemoveFromCart = once(function(){
    // 1) Via cart change/update endpoint
    const _fetch = window.fetch;
    window.fetch = function(input, init){
      const url = (typeof input === 'string') ? input : (input && input.url) || '';
      if (/cart\/change\.js/i.test(url)) {
        try {
          const body = init && init.body;
          if (body) {
            let qty = null, line = null;
            if (body instanceof FormData) {
              qty = body.get('quantity');
              line = body.get('line');
            } else if (typeof body === 'string') {
              const params = new URLSearchParams(body);
              qty = params.get('quantity');
              line = params.get('line');
            }
            if (qty === '0') {
              safeFbq('trackCustom', 'Removed_From_Cart', payloadFromContext({ line: line }));
            }
          }
        } catch (e) {
          console && console.warn && console.warn('Remove cart parse error', e);
        }
      }
      return _fetch.apply(this, arguments);
    };

    // 2) Direct "remove" buttons that might bypass fetch
    document.addEventListener('click', (e) => {
      const clickSel = '[href*="cart/change"], [data-cart-remove], button[name="remove"], button[data-cart-remove]';
      if (e.target.closest(clickSel)) {
        // Fire a tentative event; fetch patch above will also catch final state
        safeFbq('trackCustom', 'Removed_From_Cart', { reason: 'remove_button', url: location.pathname });
      }
    }, true);
  });

  // --- Subscribe (Klaviyo) ---------------------------------------------------
  const wireSubscribe = once(function(){
    // 1) Klaviyo Forms SDK emits events on window
    if (window._klOn) {
      window._klOn('formSuccess', () => {
        safeFbq('trackCustom', 'Subscribe', { provider: 'klaviyo' });
      });
    }
    // 2) Fallback: detect requests to Klaviyo endpoints
    const _fetch = window.fetch;
    window.fetch = function(input, init){
      const url = (typeof input === 'string') ? input : (input && input.url) || '';
      if (/klaviyo|kmail/i.test(url)) {
        safeFbq('trackCustom', 'Subscribe', { provider: 'klaviyo' });
      }
      return _fetch.apply(this, arguments);
    };
  });

  // --- Customize Start / Complete (HulkApps options) ------------------------
  const wireCustomize = once(function(){
    // HulkApps (or similar) often dispatches custom events; adjust if your app uses different hooks
    document.addEventListener('product-options:open', () => {
      safeFbq('trackCustom', 'Customize_Start', payloadFromContext());
    });

    document.addEventListener('product-options:submit', () => {
      safeFbq('trackCustom', 'Customize_Complete', payloadFromContext());
    });
  });

  // --- PDP View (custom detail view events) ---------------------------------
  const firePDPView = () => {
    if (!isPDP()) return;
    const base = payloadFromContext();
    // Custom PDP view event; standard ViewContent is handled by Shopify’s native integration
    safeFbq('trackCustom', 'View_Item', base);
  };

  // --- Collection View ------------------------------------------------------
  const fireCollectionView = () => {
    if (!isCollection()) return;
    const base = payloadFromContext({
      collection_handle: meta.collection && meta.collection.handle
    });
    safeFbq('trackCustom', 'View_Item_List', base);
  };

  // --- Blog View ------------------------------------------------------------
  const fireBlogView = () => {
    if (!isBlog()) return;
    safeFbq('trackCustom', 'View_Blog_Articles', payloadFromContext());
  };

  // --- Bootstrapping --------------------------------------------------------
  const boot = () => {
    wireScrollDepth();
    wireSizeGuide();
    wireSearch();
    wireWishlist();
    wireAddToCart();
    wireRemoveFromCart();
    // wireBeginCheckout removed – rely on Shopify's InitiateCheckout standard event
    wireSubscribe();
    wireCustomize();
    firePDPView();
    fireCollectionView();
    fireBlogView();
  };

  onReady(boot);
  document.addEventListener('route:change', () => setTimeout(boot, 50));
})();
